/**
 * FilePreview — compact card with a "Vorschau" CTA that opens an embedded
 * preview in a modal pop-up, plus a "Herunterladen" button.
 *
 * Why a modal pop-up:
 *   - The default state stays compact (~64px tall card) so a material with 3
 *     attached PDFs doesn't take up half the screen.
 *   - When the user clicks "Vorschau", we open a centered modal with the
 *     embedded preview (image, PDF iframe, video). Closing the modal restores
 *     the compact view. Audios are NEVER wrapped here — they render inline
 *     because pressing play is sufficient (no separate "open" gesture needed).
 *
 * Auth: the preview URL is built via fileUrl() which appends ?auth=<token>
 * so it works in privacy-strict browsers (DuckDuckGo, Brave, in-app webviews).
 */
import React, { useState, useEffect } from 'react';
import {
  FileImage, FilePdf, FileDoc, FileXls, FilePpt,
  FileArchive, File as FileGeneric, MicrophoneStage, VideoCamera,
  DownloadSimple, Eye, X,
} from '@phosphor-icons/react';
import { fileUrl as buildFileUrl } from '../utils/fileUrl';

const ext = (name = '') => (name.split('.').pop() || '').toLowerCase();

function detectKind(file) {
  const t = (file.type || file.content_type || '').toLowerCase();
  const e = ext(file.name || file.url || '');
  if (t.startsWith('image/') || /^(jpg|jpeg|png|gif|webp|heic|svg|bmp)$/i.test(e)) return 'image';
  if (t === 'application/pdf' || e === 'pdf') return 'pdf';
  if (t.startsWith('audio/') || /^(mp3|wav|ogg|m4a|aac|opus|flac|webm)$/i.test(e)) return 'audio';
  if (t.startsWith('video/') || /^(mp4|mov|m4v|avi|mpg|mpeg)$/i.test(e)) return 'video';
  if (/^(docx?|odt|rtf)$/i.test(e) || /word/i.test(t)) return 'word';
  if (/^(xlsx?|csv|ods)$/i.test(e) || /spreadsheet|excel/i.test(t)) return 'excel';
  if (/^(pptx?|odp)$/i.test(e) || /powerpoint|presentation/i.test(t)) return 'powerpoint';
  if (/^(zip|rar|7z|tar|gz)$/i.test(e)) return 'archive';
  return 'other';
}

function iconFor(kind) {
  switch (kind) {
    case 'image': return FileImage;
    case 'pdf': return FilePdf;
    case 'word': return FileDoc;
    case 'excel': return FileXls;
    case 'powerpoint': return FilePpt;
    case 'audio': return MicrophoneStage;
    case 'video': return VideoCamera;
    case 'archive': return FileArchive;
    default: return FileGeneric;
  }
}

function humanSize(bytes) {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Pop-up that hosts the embedded preview ───────────────────────────────────
function PreviewModal({ open, onClose, kind, src, filename, isDe }) {
  // Close on Escape key for keyboard users; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="file-preview-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 border-b border-stone-200 px-4 py-2.5">
          <p className="text-sm font-semibold text-[#292524] truncate">{filename}</p>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <a
              href={src}
              download={filename}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs bg-white border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-50 font-medium text-[#292524]"
              data-testid="modal-download"
            >
              <DownloadSimple size={12} weight="bold" />
              {isDe ? 'Herunterladen' : 'Descargar'}
            </a>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-stone-100 text-stone-500"
              aria-label={isDe ? 'Schließen' : 'Cerrar'}
              data-testid="modal-close"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-stone-50">
          {kind === 'image' && (
            // Use natural sizing — never crop. The image scales DOWN to fit
            // the viewport width but keeps its full aspect ratio, and any
            // extra height scrolls naturally inside this overflow-auto box.
            // On desktop this prevents a flex layout from squishing a tall
            // image into a short viewport and hiding the bottom.
            <div className="min-h-full w-full flex items-start justify-center p-2">
              <img
                src={src}
                alt={filename}
                className="max-w-full h-auto w-auto block"
                style={{ imageOrientation: 'from-image' }}
              />
            </div>
          )}
          {kind === 'pdf' && (
            <iframe
              src={src + '#toolbar=1&navpanes=0'}
              title={filename}
              className="w-full h-full"
              style={{ border: 0, minHeight: '70vh' }}
            />
          )}
          {kind === 'video' && (
            <div className="min-h-full w-full flex items-center justify-center p-2">
              <video controls autoPlay src={src} className="max-w-full max-h-[80vh] bg-black" />
            </div>
          )}
          {!['image', 'pdf', 'video'].includes(kind) && (
            <div className="text-center p-8 text-[#57534E]">
              <p className="text-sm">
                {isDe
                  ? 'Vorschau für dieses Format nicht verfügbar. Bitte herunterladen.'
                  : 'Vista previa no disponible para este formato. Descárgalo.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FilePreview({ file, lang = 'de' }) {
  // Hooks MUST be called unconditionally — so we run the rules-of-hooks
  // friendly setup first, then short-circuit with a placeholder render if
  // the caller passed a missing/invalid `file` prop. This protects the
  // whole page from blanking out when a sibling component wires the prop
  // incorrectly (e.g. `f={...}` instead of `file={...}`).
  const [open, setOpen] = useState(false);
  const isDe = lang !== 'es';

  if (!file || typeof file !== 'object') {
    if (typeof console !== 'undefined') {
      console.warn('FilePreview: missing or invalid `file` prop', file);
    }
    return (
      <div className="bg-stone-100 border border-stone-200 rounded-lg p-3 text-xs text-stone-500 italic" data-testid="file-preview-empty">
        {isDe ? 'Datei nicht verfügbar' : 'Archivo no disponible'}
      </div>
    );
  }
  const kind = detectKind(file);
  const Icon = iconFor(kind);
  const src = buildFileUrl(file.url || file.content_url || file.path || '');
  const filename = file.name || file.filename || (file.url || '').split('/').pop() || (isDe ? 'Datei' : 'Archivo');
  const previewable = ['image', 'pdf', 'video'].includes(kind);

  // Audios render inline (pressing play is enough — no separate preview UI).
  if (kind === 'audio') {
    return (
      <audio
        controls
        preload="metadata"
        src={src}
        className="h-9 max-w-md w-full rounded-full"
        style={{ maxHeight: '36px' }}
        data-testid={`file-preview-audio-${filename}`}
      />
    );
  }

  return (
    <>
      <div
        className="bg-stone-50 border border-stone-200 rounded-lg p-3 flex items-center gap-3"
        data-testid={`file-preview-${kind}-${filename}`}
      >
        <Icon size={28} weight="duotone" className="text-[#AB1C24] flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[#292524] truncate">{filename}</p>
          <p className="text-[11px] text-[#A8A29E]">{kind.toUpperCase()} · {humanSize(file.size)}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {previewable && (
            <button
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-1 text-xs bg-white border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-50 font-medium text-[#292524]"
              data-testid={`preview-${filename}`}
            >
              <Eye size={12} weight="bold" />
              {isDe ? 'Vorschau' : 'Vista previa'}
            </button>
          )}
          <a
            href={src}
            download={filename}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs bg-white border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-50 font-medium text-[#292524]"
            data-testid={`download-${filename}`}
          >
            <DownloadSimple size={12} weight="bold" />
            {isDe ? 'Herunterladen' : 'Descargar'}
          </a>
        </div>
      </div>
      <PreviewModal
        open={open}
        onClose={() => setOpen(false)}
        kind={kind}
        src={src}
        filename={filename}
        isDe={isDe}
      />
    </>
  );
}
